import {
  FormattedHTMLMessage,
  FormattedMessage,
  defineMessages,
  injectIntl,
} from 'react-intl';
import { MapInteraction } from 'react-map-interaction';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';

import { FloatingModal } from '../../components/Modal';
import { claimSeat } from '../../../../ducks/seatmap';
import { getPublicUser } from '../../../../ducks/users';
import { getTicketAsAdmin, getTickets } from '../../../../ducks/event';
import { navigate } from '../../../../helpers';
import Input from '../../../Account/components/Input';
import Loader from '../../components/Loader';
import Option from '../components/Option';
import Seats from './Seats';
import dhid from '../../../../dhid';
import mapControlStyles from '../../Event/Map/map.scss';
import parentStyles from '../seatmap.scss';
import styles from './map.scss';
import withUser from '../../../../components/withUser';

const propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      eventId: PropTypes.string.isRequired,
      ticketId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  seatmapError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  dispatch: PropTypes.func.isRequired,
  tickets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
  })),
  event: PropTypes.shape({
    id: PropTypes.string,
    shortcode: PropTypes.string,
  }),
  loading: PropTypes.bool,
  seatmapLoading: PropTypes.bool,
  user: PropTypes.shape({}),
  users: PropTypes.objectOf(PropTypes.shape({ name: PropTypes.string })),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
};

const messages = defineMessages({
  lockedMessage: {
    id: 'seatmap.map.lockedMessage',
    defaultMessage: 'Your ticket is locked and cannot be changed.',
  },
  lockedTitle: {
    id: 'seatmap.map.lockedTitle',
    defaultMessage: 'Your ticket is locked',
  },
  claimedMessage: {
    id: 'seatmap.map.claimedMessage',
    defaultMessage: 'This seat ({rowName}:{seatName}) is taken by someone else. Please choose another seat.',
  },
  claimedTitle: {
    id: 'seatmap.map.claimedTitle',
    defaultMessage: 'This seat is taken',
  },
  validSeatMessage: {
    id: 'seatmap.map.validSeatMessage',
    defaultMessage: 'Your ticket does not give you access to this type of seat ({seatName}).',
  },
  oneGapMessage: {
    id: 'seatmap.map.oneGapMessage',
    defaultMessage: 'You are not allowed to create a single seat gap.',
  },
  passwordTitle: {
    id: 'seatmap.map.passwordTitle',
    defaultMessage: 'Reserved seat',
  },
  passwordMessage: {
    id: 'seatmap.map.passwordMessage',
    defaultMessage: 'Choose this seat ({rowName}:{seatName}) by entering a private password given to you by the organizers.',
  },
  chooseThisSeat: {
    id: 'seatmap.map.chooseThisSeat',
    defaultMessage: 'Choose this seat',
  },
  seatUnavailable: {
    id: 'seatmap.map.seatUnavailable',
    defaultMessage: 'This seat is unavailable',
  },
  passwordLabel: {
    id: 'seatmap.map.passwordLabel',
    defaultMessage: 'Password',
  },
  passwordPlaceholder: {
    id: 'seatmap.map.passwordPlaceholder',
    defaultMessage: 'Type the password here',
  },
  claimSeat: {
    id: 'seatmap.map.claimSeat',
    defaultMessage: 'Claim seat',
  },

  availableSeatTitle: {
    id: 'seatmap.map.availableSeatTitle',
    defaultMessage: '{rowName}:{seatName} is available',
  },
  reservedSeat: {
    id: 'seatmap.map.reservedSeat',
    defaultMessage: 'This seat is reserved by a group or organization',
  },
  sitsHere: {
    id: 'seatmap.map.sitsHere',
    defaultMessage: '{name} sits here',
  },
});

const calculateTranslation = (baseHeight, baseWidth, rotation) => {
  // The math behind positioning rotated shapes differs based on the rotation
  // due to where the axis of the rotation is (top left corner).
  const r = ((360 + rotation) % 360);
  const th = r * (Math.PI / 180);
  let translateX = 0;
  let translateY = 0;
  if (r <= 90) { // 0-90
    translateX = (baseHeight * Math.sin(th));
    translateY = 0;
  } else if (r <= 180) { // 90 - 180
    translateX = (baseHeight * Math.sin(th)) - (baseWidth * Math.cos(th));
    translateY = -(baseHeight * Math.cos(th));
  } else if (r <= 270) { // 180 - 270
    translateX = -(baseWidth * Math.cos(th));
    translateY = -(baseWidth * Math.sin(th)) - (baseHeight * Math.cos(th));
  } else { // 270 - 360
    translateX = 0;
    translateY = -(baseWidth * Math.sin(th));
  }

  return {
    translateX,
    translateY,
  };
};

const offsetXYtoCenter = (seat, type) => {
  const translation = calculateTranslation(type.height, type.width, seat.rotation);

  const th = ((360 + seat.rotation) % 360) * (Math.PI / 180);
  const rotatedWidth = Math.abs(type.height * Math.sin(th)) + Math.abs(type.width * Math.cos(th));
  const rotatedHeight = Math.abs(type.width * Math.sin(th)) + Math.abs(type.height * Math.cos(th));

  return {
    ...seat,
    x: (seat.x + (type.width / 2)) - ((type.width - rotatedWidth) / 2) - translation.translateX,
    y: (seat.y + (type.height / 2)) - ((type.height - rotatedHeight) / 2) - translation.translateY,
  };
};

class Map extends React.Component {
  constructor(props) {
    super(props);

    const forced = props.history.location.search === '?force';

    this.state = {
      seatmap: undefined,
      activeFloor: undefined,
      MIScale: 0.5,
      MITranslation: { x: 0, y: 0 },
      activeSeat: undefined,
      // floorIndex: 0,
      activeTicket: undefined,
      activeTicketSeat: undefined,
      passwordModalOpen: false,
      password: undefined,
      forced,
    };
  }

  componentDidMount() {
    const {
      dispatch,
      match: { params },
      event,
      tickets,
      user,
    } = this.props;
    const { ticketId } = params;

    if (event && !(tickets?.length > 0) && user) {
      if (this.state.forced && ticketId) {
        dispatch(getTicketAsAdmin(event.id, ticketId));
      } else {
        dispatch(getTickets(event.id));
      }
    }

    if (event) {
      this.getSeatmap(event.id);
    }

    this.setActiveTicket(tickets, ticketId);
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const {
      dispatch,
      match: { params },
      event,
      tickets,
    } = newProps;
    const { ticketId } = params;
    const {
      event: oldEvent,
      match: oldMatch,
      tickets: oldTickets,
    } = this.props;
    const oldTicketId = oldMatch && oldMatch.params && oldMatch.params.ticketId;

    if ((!oldEvent && event) || (oldEvent && oldEvent.id !== event.id)) {
      if (this.state.forced && ticketId) {
        dispatch(getTicketAsAdmin(event.id, ticketId));
      } else {
        dispatch(getTickets(event.id));
      }
      this.getSeatmap(event.id);
    }

    if ((tickets !== oldTickets) || (ticketId !== oldTicketId)) {
      this.setActiveTicket(tickets, ticketId);
    }
  }

  onPressSeat = (seat) => {
    const {
      activeSeat,
    } = this.state;
    const {
      dispatch,
    } = this.props;

    const newActiveSeat = (activeSeat && seat && activeSeat.id === seat.id) ? undefined : seat;
    if (newActiveSeat && newActiveSeat.claimed_by) {
      dispatch(getPublicUser(newActiveSeat.claimed_by));
    }

    this.setState({
      // If this is the selected seat, or if there is not seat, set the active
      // seat to undefined, enabling a "toggle".
      activeSeat: newActiveSeat,
    });
  }

  onCloseSeat = () => {
    this.setState({
      activeSeat: undefined,
    });
  }

  onClaimSeat = (seat) => {
    const { dispatch, history, event } = this.props;
    const {
      activeTicket,
      activeTicketSeat,
      forced,
      password,
    } = this.state;
    const isSeatValid = this.isSeatValid(seat);
    const pushTo = `/tickets/${event.shortcode}/place/${activeTicket.id}/done`;

    if (activeTicketSeat && seat.id === activeTicketSeat.id) {
      // If this is the same seat as we already have on the ticket, don't bother claiming
      navigate(history, {
        pathname: pushTo,
      });
    } else if (!isSeatValid.protected || (isSeatValid.protected && password)) {
      // Else, if this seat is protected and password entered, or otherwise unprotected
      dispatch(claimSeat(event.id, activeTicket.id, seat.id, password))
        .then((success) => {
          if (success) {
            navigate(history, {
              pathname: pushTo,
              state: {
                forced,
              },
            });
          }
        });
    } else {
      // If password protected and no password entered, open up the password modal
      this.setState({
        passwordModalOpen: true,
        password: undefined,
      });
    }
  }

  setActiveTicket(tickets, activeTicketId) {
    if (tickets && tickets?.length > 0) {
      const activeTicket = tickets.find((ticket) => (
        ticket.id === activeTicketId
      ));
      const activeTicketSeat = activeTicket && activeTicket.expanded_seat;

      this.setState({
        activeTicket,
        activeTicketSeat,
      }, () => {
        if (activeTicketSeat) {
          this.onPressSeat(activeTicketSeat);
        }
      });
    }
  }

  getSeatmap(eventId) {
    const extraUrl = this.state.forced ? 'admin-' : '';
    dhid.get(`${dhid.defaults.baseURL}/1/content/event/${eventId}/${extraUrl}seatmap`)
      .then(({ data }) => {
        const sortedFloors = data && data.floors
          && Object.values(data.floors).sort((a, b) => a.order - b.order);
        const { activeSeat } = this.state;
        // If there is an active seat, get its floor and its index in the sorted
        // floors to set it as the initial floor shown.
        const activeTicketFloorIndex = activeSeat
          && sortedFloors.findIndex((floor) => (
            floor.id === activeSeat.expanded_row.expanded_section.floor_id
          ));
        const floorIndex = activeTicketFloorIndex || 0;
        // Next check might be excessive, but it seems a lot of ppl like to
        // poke around to try to find the seatmap before it's released.
        const activeFloor = sortedFloors && sortedFloors?.length > floorIndex
          ? sortedFloors[floorIndex]
          : null;
        this.setState({
          seatmap: data,
          activeFloor,
          sortedFloors,
          // floorIndex,
        });
      });
  }

  switchFloor = (index) => {
    const { sortedFloors } = this.state;
    const newFloor = sortedFloors[index];
    this.setState({
      activeFloor: newFloor,
      // floorIndex: index,
      MIScale: 0.5,
      MITranslation: { x: 0, y: 0 },
      activeSeat: undefined,
    });
  }

  isSeatValid = (seat) => {
    const {
      activeFloor,
      activeTicket,
      forced,
      seatmap,
    } = this.state;
    const { intl: { formatMessage }, users } = this.props;
    const validSeats = seatmap && activeTicket
      && seatmap.valid_seats_per_ticket_type[activeTicket.ticket_type_id];
    const seatTypes = seatmap && seatmap.seat_types;
    const activeSection = seat && seatmap && activeFloor
      && seatmap.sections[activeFloor.id][seat.section_id];
    const rows = seatmap && seatmap.rows && activeFloor && seatmap.rows[activeFloor.id];

    const seatInfo = {
      seatType: seatTypes && seatTypes[seat.seat_type_id].name,
      rowName: rows && rows[seat.row_id]?.name,
      seatName: seat && seat.name,
    };

    if (forced && !seat.claimed) {
      // I'm assuing the backend will handle rejection if someone finds this parameter
      return {
        valid: true,
      };
    }

    if (activeTicket && activeTicket.locked) {
      // Ticket is locked
      return {
        valid: false,
        message: formatMessage(messages.lockedMessage),
        title: formatMessage(messages.lockedTitle),
      };
    } else if (activeTicket && activeTicket.claimed_seat === seat.id) {
      // Ticket already has this seat
      return {
        valid: true,
      };
    } else if (seat.claimed) {
      // Seat has been claimed by someone else
      return {
        valid: false,
        message: formatMessage(messages.claimedMessage, seatInfo),
        description:
          users
          && users.get(seat.claimed_by)
          && formatMessage(messages.sitsHere, { name: users.getIn([seat.claimed_by, 'name']) }),
        title: formatMessage(messages.claimedTitle),
      };
    } else if (seat.protected || rows[seat.row_id].protected) {
      return {
        valid: true,
        protected: true,
        title: formatMessage(messages.passwordTitle),
        message: formatMessage(messages.passwordMessage, seatInfo),
        description: formatMessage(messages.reservedSeat, seatInfo),
        icon: 'key',
      };
    } else if (validSeats && !validSeats.includes(seat.id)) {
      // Seat type is not available for this ticket type
      return {
        valid: false,
        message: formatMessage(
          messages.validSeatMessage,
          { seatName: seatTypes[seat.seat_type_id].name },
        ),
      };
    } else if (activeTicket && seat.order && this.isOneGap(seat)) {
      // Dont allow people to make gaps
      return {
        valid: false,
        message: formatMessage(messages.oneGapMessage),
      };
    } else if (activeSection && activeSection.protected) {
      // Seat is in a section that is password protected
      return {
        valid: true,
        protected: true,
        title: formatMessage(messages.passwordTitle),
        message: formatMessage(messages.passwordMessage, seatInfo),
        description: formatMessage(messages.reservedSeat, seatInfo),
        icon: 'key',
      };
    }
    return {
      valid: true,
    };
  };

  isOneGap = (seat) => !(
    this.isTaken(seat, 1)
      || this.isTaken(seat, -1)
      || (!this.isTaken(seat, 2) && !this.isTaken(seat, -2))
      || (this.isTaken(seat, 2) && this.isTaken(seat, -2))
  );

  isTaken = (seat, relativePosition) => {
    const { seatmap, activeFloor } = this.state;
    const relativeSeat = Object.values(seatmap.seats[activeFloor.id])
      .find((s) => s.row_id === seat.row_id && s.order === seat.order + relativePosition);
    return (relativeSeat && relativeSeat.claimed) || false;
  }

  render() {
    const {
      seatmap,
      activeFloor,
      MIScale,
      MITranslation,
      activeSeat,
      sortedFloors,
      // floorIndex,
      activeTicketSeat,
      activeTicket,
      passwordModalOpen,
      password,
      forced,
    } = this.state;
    const {
      seatmapError,
      loading,
      seatmapLoading,
      intl,
    } = this.props;

    // The code below this statement is not ready for handling an empty this.state
    // This guard clause will ensure the code below doesn't explode during first component states.
    if (seatmap == null) {
      return (<></>);
    }

    const { formatMessage } = intl;
    const shapes = activeFloor && seatmap.floor_shapes && seatmap.floor_shapes[activeFloor.id]
      && Object.values(seatmap.floor_shapes[activeFloor.id]);
    const rows = seatmap && seatmap.rows && activeFloor && seatmap.rows[activeFloor.id];
    const seatTypes = seatmap && seatmap.seat_types;
    const sections = seatmap && activeFloor
      && seatmap.sections[activeFloor.id];
    const isChosenSeatActiveTicketSeat = activeTicketSeat && activeSeat
      && activeTicketSeat.id === activeSeat.id;
    const isSeatValid = activeSeat && this.isSeatValid(activeSeat);

    // =========== <Seats> dependencies

    // Ensure null safety or UI will crash! >> DH-434
    const seats = (seatmap && seatmap.seats && activeFloor && seatmap.seats[activeFloor.id]
      && Object.values(seatmap.seats[activeFloor.id])) ?? [];

    const validSeatsPerTicketType = seatmap && seatmap.valid_seats_per_ticket_type;

    // Named dependency because is used at <Seats>
    const dependencyValidSeats = forced
      ? seats.map((s) => s.id)
      : activeTicket && validSeatsPerTicketType[activeTicket.ticket_type_id];

    const seatTypesOnThisFloor = seats && seats.reduce((acc, seat) => ({
      ...acc,
      [seat.seat_type_id]: true,
    }), {});

    // Named dependency because is used at <Seats>
    const dependencySeatTypes = seatTypesOnThisFloor && Object.keys(seatTypesOnThisFloor)
      .reduce((obj, key) => ({ ...obj, [key]: seatTypes[key] }), {});

    // End of <Seats> dependencies  ===========

    const diagonal = (dX, dY) => Math.sqrt((dX * dX) + (dY * dY));

    // Active and selected seat indicators
    const showActiveTicketSeat = activeTicketSeat && activeFloor && activeTicket.expanded_seat
      .expanded_row.expanded_section.floor_id === activeFloor.id;
    const showSelectedSeat = activeSeat && !isChosenSeatActiveTicketSeat;
    const showArrow = showActiveTicketSeat && showSelectedSeat;
    const activeTicketDiameter = showActiveTicketSeat && diagonal(
      seatTypes[activeTicketSeat.seat_type_id].width,
      seatTypes[activeTicketSeat.seat_type_id].height,
    ) + 10;
    const selectedSeatDiameter = showSelectedSeat && diagonal(
      seatTypes[activeSeat.seat_type_id].width,
      seatTypes[activeSeat.seat_type_id].height,
    ) + 10;
    const patchedActiveTicketSeat = activeTicketSeat && seatTypes
      && offsetXYtoCenter(activeTicketSeat, seatTypes[activeTicketSeat.seat_type_id]);
    const patchedActiveSeat = activeSeat && seatTypes
      && offsetXYtoCenter(activeSeat, seatTypes[activeSeat.seat_type_id]);

    const arrow = {};
    if (showArrow) {
      arrow.x1 = patchedActiveTicketSeat.x;
      arrow.y1 = patchedActiveTicketSeat.y;
      arrow.x2 = patchedActiveSeat.x;
      arrow.y2 = patchedActiveSeat.y;
      arrow.s1 = 80;
      arrow.s2 = 80;
      const dX = arrow.x2 - arrow.x1;
      const dY = arrow.y2 - arrow.y1;
      arrow.angle = -90 + ((Math.atan2(dY, dX) * 180) / Math.PI);
      arrow.length = Math.max(arrow.s1 + 11, diagonal(dX, dY));
    }

    let canvasScale = 1;
    if (activeFloor && (activeFloor.width >= 4000 || activeFloor.height >= 4000)) {
      canvasScale = Math.max(activeFloor.width, activeFloor.height) / 4000;
    }

    return (
      <>
        <Loader loading={loading || seatmapLoading || !seatmap} />
        { (!loading && !seatmapLoading) && seatmap && activeFloor
          && (
            <>
              { activeTicket
              && (
              <div className={classNames(
                parentStyles.title,
                styles.title,
              )}
              >
                <FormattedMessage
                  id="seatmap.map.where_sit"
                  defaultMessage="Where do you want to sit?"
                />
              </div>
              )}
              <div className={styles.mapContainer}>

                { sortedFloors && sortedFloors?.length > 1
                  && (
                  <div className={styles.tabs}>
                    { sortedFloors.map((floor, index) => (
                      <div
                        onClick={() => this.switchFloor(index)}
                        role="button"
                        tabIndex="0"
                        key={floor.id}
                        className={classNames(
                          styles.tab,
                          {
                            [`${styles.active}`]: floor.id === activeFloor.id,
                          },
                        )}
                      >
                        {floor.name}
                      </div>
                    ))}
                  </div>
                  )}
                { activeSeat && activeTicket
                && (
                <div className={styles.activeSeat}>
                  { isSeatValid.valid
                    ? (
                      <Option
                        icon={isSeatValid.icon || 'map-pin'}
                        title={isSeatValid.title || formatMessage(messages.chooseThisSeat)}
                        onClick={() => this.onClaimSeat(activeSeat)}
                      >
                        { isSeatValid.message
                          ? <span>{isSeatValid.message}</span>
                          : (
                            <FormattedHTMLMessage
                              id="seatmap.map.chooseSeat"
                              defaultMessage="Choose a <strong>{seatType}</strong> at <strong>{rowName}:{seatName}</strong> as your seat."
                              values={{
                                seatType: seatTypes[activeSeat.seat_type_id].name,
                                rowName: rows[activeSeat.row_id].name,
                                seatName: activeSeat.name,
                              }}
                            />
                          )}
                      </Option>
                    )
                    : (
                      <Option
                        icon="exclamation-circle"
                        title={isSeatValid.title || formatMessage(messages.seatUnavailable)}
                        disabled
                      >
                        {isSeatValid.message}
                      </Option>
                    )}
                </div>
                )}
                { activeSeat && !activeTicket
                && (
                <div className={styles.activeSeat}>
                  <Option
                    icon={isSeatValid.icon || 'map-pin'}
                    title={
                      (isSeatValid && isSeatValid.title)
                      || formatMessage(messages.availableSeatTitle, {
                        rowName: rows[activeSeat.row_id].name,
                        seatName: activeSeat.name,
                      })
                    }
                  >
                    { isSeatValid && (!isSeatValid.valid || isSeatValid.description)
                      ? <span>{isSeatValid.description}</span>
                      : (
                        <FormattedHTMLMessage
                          id="seatmap.map.viewSeat"
                          defaultMessage="This is a free seat of type <strong>{seatType}</strong>."
                          values={{
                            seatType: seatTypes[activeSeat.seat_type_id].name,
                          }}
                        />
                      )}
                  </Option>
                </div>
                )}
                <MapInteraction
                  scale={MIScale}
                  translation={MITranslation}
                  minScale={0.1}
                  maxScale={2}
                  onChange={({ scale, translation }) => this.setState({
                    MIScale: scale,
                    MITranslation: translation,
                  })}
                  showControls
                  plusBtnContents={<i className="far fa-search-plus" />}
                  minusBtnContents={<i className="far fa-search-minus" />}
                  controlsClass={classNames(mapControlStyles.controls, styles.controls)}
                >
                  {() => (
                    <div
                      className={styles.scalableContainer}
                      style={{
                        transform: `translate3d(${MITranslation.x}px, ${MITranslation.y}px, 0) scale3d(${MIScale}, ${MIScale}, ${MIScale})`,
                      }}
                    >
                      <Seats
                        key={activeFloor.id}
                        seats={seats}
                        rows={rows}
                        sections={sections}
                        seatTypes={dependencySeatTypes}
                        width={activeFloor.width}
                        height={activeFloor.height}
                        canvasScale={canvasScale}
                        scale={MIScale}
                        onPressSeat={(seat) => this.onPressSeat(seat)}
                        activeTicket={activeTicket}
                        validSeats={dependencyValidSeats}
                      >
                        { // Show a yellow flash over the seat that is selected for this ticket
                        showActiveTicketSeat
                          && (
                          <div
                            className={classNames({
                              [styles.circle]: true,
                              [styles.flash]: !showSelectedSeat,
                            })}
                            style={{
                              top: `${((patchedActiveTicketSeat.y) - (activeTicketDiameter / 2)) / canvasScale}px`,
                              left: `${((patchedActiveTicketSeat.x) - (activeTicketDiameter / 2)) / canvasScale}px`,
                              height: `${activeTicketDiameter / canvasScale}px`,
                              width: `${activeTicketDiameter / canvasScale}px`,
                              borderWidth: `${4 / canvasScale}px`,
                            }}
                          />
                          )
                      }
                        { // Show a white flash over the seat that was selected by the
                        // user (unless it's already yellow)
                        showSelectedSeat
                        && (
                        <div
                          className={classNames({
                            [styles.circle]: true,
                            [styles.invalid]: activeTicket && !isSeatValid.valid,
                          })}
                          style={{
                            top: `${((patchedActiveSeat.y) - (selectedSeatDiameter / 2)) / canvasScale}px`,
                            left: `${((patchedActiveSeat.x) - (selectedSeatDiameter / 2)) / canvasScale}px`,
                            height: `${selectedSeatDiameter / canvasScale}px`,
                            width: `${selectedSeatDiameter / canvasScale}px`,
                            borderWidth: `${4 / canvasScale}px`,
                          }}
                        />
                        )
                      }

                        {
                        rows && Object.values(rows) && Object.values(rows).map((row) => (
                          <div
                            key={row.id}
                            className={styles.row}
                            style={{
                              transform: `translate(${(row.x - (row.font_size * row.name?.length * 0.7)) / canvasScale}px, ${(row.y - row.font_size) / canvasScale}px)`,
                              fontSize: `${(row.font_size * 0.8) / canvasScale}px`,
                            }}
                          >
                            {row.name}
                          </div>
                        ))
                      }
                        { shapes && shapes.map((shape) => (
                          <div
                            key={shape.id}
                            className={styles.shape}
                            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
                              __html: seatmap.shapes && seatmap.shapes[shape.shape_id]
                              && seatmap.shapes[shape.shape_id].data,
                            }}
                            style={{
                              width: activeFloor.width,
                              transform: `rotate(${shape.rotation}deg) scale(${shape.scale / canvasScale}) translate3d(${shape.x / (shape.scale / canvasScale) / canvasScale}px, ${shape.y / (shape.scale / canvasScale) / canvasScale}px, ${shape.z / shape.scale}px)`,
                            }}
                          />
                        )) }

                        { // Show an arrow pointing from the original seat to the new one
                        showArrow
                        && (
                        <div
                          style={{
                            top: `${arrow.y1 / canvasScale}px`,
                            left: `${arrow.x1 / canvasScale}px`,
                            height: `${arrow?.length / canvasScale}px`,
                            transform: `rotate(${arrow.angle}deg)`,
                            position: 'absolute',
                            zIndex: 9,
                            transformOrigin: 'center top',
                            paddingTop: `${((activeTicketDiameter / 2) - 2) / canvasScale}px`,
                            paddingBottom: `${selectedSeatDiameter / 2 / canvasScale}px`,
                          }}
                        >
                          <div
                            className={classNames({
                              [styles.arrow]: true,
                              [styles.invalid]: !isSeatValid.valid,
                            })}
                          />
                        </div>
                        )
                      }
                      </Seats>
                    </div>
                  )}
                </MapInteraction>
                { activeSeat && activeTicket && isSeatValid.protected
                && (
                <FloatingModal
                  isOpen={passwordModalOpen}
                  onRequestClose={() => this.onPressSeat(activeSeat)}
                >
                  <div className={parentStyles.modal}>
                    <div className={parentStyles.title}>
                      <FormattedMessage
                        id="seatmap.map.password.title"
                        defaultMessage="Reserved seat"
                      />
                    </div>
                    <div className={parentStyles.description}>
                      <FormattedMessage
                        id="seatmap.map.password.description"
                        defaultMessage="This seat is reserved and requires a password. If you have received a password, enter it here to claim the seat."
                      />
                    </div>
                    <div className={parentStyles.input}>
                      <Input
                        id="password"
                        label={formatMessage(messages.passwordLabel)}
                        placeholder={formatMessage(messages.passwordPlaceholder)}
                        type="text"
                        onChange={(event) => this.setState({ password: event.target.value })}
                        errors={seatmapError && seatmapError.response
                          && seatmapError.response.data}
                        stretch
                      />
                    </div>
                    <Option
                      icon="map-pin"
                      title={formatMessage(messages.claimSeat)}
                      onClick={() => this.onClaimSeat(activeSeat)}
                      disabled={!password || password?.length < 1}
                    />
                  </div>
                </FloatingModal>
                )}
              </div>
            </>
          )}
      </>
    );
  }
}

Map.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    event: state.getIn(['event', 'data']),
    error: state.getIn(['event', 'error']),
    seatmapError: state.getIn(['seatmap', 'error']),
    seatmapLoading: state.getIn(['seatmap', 'loading']),
    loading: state.getIn(['event', 'loading']),
    tickets: state.getIn(['event', 'tickets']),
    users: state.getIn(['users', 'list']),
  };
}

export default injectIntl(withUser(connect(mapStateToProps)(Map)));
